import { ICON_PROPS, formatRelative, notifHistory, persistNotifHistory, postToHost } from './shared.js';
import { Component, type ErrorInfo, type ReactNode, useEffect, useState } from 'react';
import {
  Accordion,
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Chip,
  Code,
  Collapse,
  Container,
  Divider,
  Drawer,
  Grid,
  Group,
  List,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NativeSelect,
  NavLink,
  NumberInput,
  Paper,
  PasswordInput,
  Progress,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArchive,
  IconArrowBackUp,
  IconBell,
  IconBolt,
  IconBrain,
  IconBriefcase,
  IconCalendar,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconCommand,
  IconDeviceMobile,
  IconDownload,
  IconEdit,
  IconFileText,
  IconFlag,
  IconFolder,
  IconGavel,
  IconHistory,
  IconMail,
  IconMailOff,
  IconMessageQuestion,
  IconMoonStars,
  IconNews,
  IconPaperclip,
  IconPencil,
  IconPhone,
  IconPlus,
  IconRadar,
  IconReload,
  IconReport,
  IconSchool,
  IconSearch,
  IconSend,
  IconSettings,
  IconShare,
  IconShield,
  IconSparkles,
  IconStar,
  IconStarFilled,
  IconTarget,
  IconTrash,
  IconUser,
  IconUserPlus,
  IconUsersGroup,
  IconWand,
  IconX,
} from '@tabler/icons-react';
import { api, setStoredToken, setStoredUser, type AuthUser } from '../../api.js';
// Round 48 follow-up (2026-07-15): AddinPage.tsx monolith split — app chrome (error boundary, login, broadcast surface, palette, sidebar),
// moved VERBATIM. Layering: shared ← shared-components ← panels ← AddinPage.

export class TabErrorBoundary extends Component<
  { name: string; children: ReactNode },
  { error: Error | null }
> {
  override state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error): { error: Error } {
    return { error };
  }
  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // biome-ignore lint/suspicious/noConsole: surface the underlying bug
    console.error(`[${this.props.name}] render error`, error, info);
  }
  override render(): ReactNode {
    if (this.state.error) {
      return (
        <Card padding="md" radius="md" withBorder bg="var(--mantine-color-red-0)">
          <Stack gap="xs">
            <Title order={5} c="red">
              {this.props.name} crashed
            </Title>
            <Text size="sm">
              {this.state.error.message || String(this.state.error)}
            </Text>
            <Text size="xs" c="dimmed">
              Open the browser console (F12) and copy the stack trace, then ping the dev. Other
              tabs still work — switch tabs to keep going.
            </Text>
            <Button
              size="xs"
              variant="default"
              onClick={() => this.setState({ error: null })}
              style={{ alignSelf: 'flex-start' }}
            >
              Try again
            </Button>
          </Stack>
        </Card>
      );
    }
    return this.props.children;
  }
}


export function AddinLogin({ onSuccess }: { onSuccess: (user: AuthUser) => void }): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [phase, setPhase] = useState<'password' | '2fa' | 'recover'>('password');
  // Round 48 follow-up (2026-07-26): addin-side account recovery. The
  // dashboard /login has had the "Locked out completely?" link since round
  // 36, but the addin's login screen never got it — a user who forgot their
  // password (or lost their authenticator) inside Outlook just saw "invalid
  // credential" with no way forward (reported 2026-07-26). This begins the
  // same email-recovery flow; the one-time link in the email opens the
  // dashboard recovery page in a browser where the user sets a new password
  // + re-pairs TOTP, then returns to the addin and signs in. Anti-enum: the
  // brain returns the same response whether or not the email exists, so the
  // success copy never confirms account existence.
  const [recoverSent, setRecoverSent] = useState(false);
  const [challenge, setChallenge] = useState<{
    challenge_id: string;
    methods: { totp: boolean; sms: boolean };
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function continuePassword() {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.authLoginPassword(email.trim().toLowerCase(), password);
      setChallenge({
        challenge_id: r.challenge_id,
        methods: { totp: !!r.methods?.totp, sms: !!r.methods?.sms },
      });
      setPhase('2fa');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  function persistToHost(token: string, user: AuthUser) {
    // Round 28 fix: persist credentials in C# registry (DPAPI) so the next
    // addin open auto-resumes the session without prompting again.
    postToHost({ type: 'persist_token', token, user });
  }

  async function beginRecovery() {
    setLoading(true);
    setErr(null);
    try {
      // Anti-enum: authRecoverBegin always resolves ok; we never learn (and
      // never reveal) whether the address is a real account.
      await api.authRecoverBegin(email.trim().toLowerCase());
      setRecoverSent(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not start recovery');
    } finally {
      setLoading(false);
    }
  }

  async function verifyTotp() {
    if (!challenge) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await api.authLogin2faTotp(challenge.challenge_id, code.trim());
      setStoredToken(r.access_token);
      setStoredUser(r.user);
      persistToHost(r.access_token, r.user);
      onSuccess(r.user);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Code rejected');
    } finally {
      setLoading(false);
    }
  }

  async function sendSms() {
    if (!challenge) return;
    try {
      await api.authLogin2faSmsSend(challenge.challenge_id);
      notifications.show({ message: 'SMS code sent.' });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'SMS failed');
    }
  }

  async function verifySms() {
    if (!challenge) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await api.authLogin2faSmsVerify(challenge.challenge_id, code.trim());
      setStoredToken(r.access_token);
      setStoredUser(r.user);
      persistToHost(r.access_token, r.user);
      onSuccess(r.user);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Code rejected');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Center style={{ minHeight: '70vh' }}>
      <Card padding="xl" radius="md" withBorder shadow="sm" w={420}>
        <Stack gap="md">
          <Stack gap={2}>
            <Title order={3}>Sign in</Title>
            <Text size="sm" c="dimmed">
              Same credentials as the dashboard.
            </Text>
          </Stack>
          {phase === 'password' ? (
            <>
              <TextInput
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                autoComplete="email"
                required
              />
              <PasswordInput
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                autoComplete="current-password"
                required
              />
              {err && (
                <Alert color="red" variant="light">
                  {err}
                </Alert>
              )}
              <Button onClick={continuePassword} loading={loading} fullWidth size="md">
                Continue
              </Button>
              <Button
                variant="subtle"
                size="xs"
                color="gray"
                onClick={() => { setPhase('recover'); setErr(null); setRecoverSent(false); }}
              >
                Locked out? (forgot password or lost authenticator)
              </Button>
            </>
          ) : phase === 'recover' ? (
            <>
              {recoverSent ? (
                <>
                  <Alert color="green" variant="light" title="Check your email">
                    If an account exists for that address, a password-reset link
                    is on its way (valid 1 hour). Open it in your browser to set a
                    new password and re-pair your authenticator, then come back
                    here and sign in.
                  </Alert>
                  <Button variant="default" onClick={() => { setPhase('password'); setRecoverSent(false); }}>
                    ← Back to sign in
                  </Button>
                </>
              ) : (
                <>
                  <Text size="sm" c="dimmed">
                    Enter your email and we'll send a reset link. It also clears a
                    lost authenticator so you can pair a new one.
                  </Text>
                  <TextInput
                    label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                    autoComplete="email"
                    required
                  />
                  {err && (
                    <Alert color="red" variant="light">
                      {err}
                    </Alert>
                  )}
                  <Button onClick={beginRecovery} loading={loading} disabled={!email.trim()} fullWidth size="md">
                    Send reset link
                  </Button>
                  <Button variant="subtle" size="xs" color="gray" onClick={() => { setPhase('password'); setErr(null); }}>
                    ← Back to sign in
                  </Button>
                </>
              )}
            </>
          ) : (
            <>
              <Text size="sm">
                {challenge?.methods.totp
                  ? '6-digit code from your authenticator app'
                  : 'Code sent to your phone'}
              </Text>
              <TextInput
                label="6-digit code"
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
                autoFocus
                inputMode="numeric"
                maxLength={8}
              />
              {err && (
                <Alert color="red" variant="light">
                  {err}
                </Alert>
              )}
              <Group grow>
                <Button onClick={verifyTotp} loading={loading} size="md">
                  Verify
                </Button>
                {challenge?.methods.sms ? (
                  <Button variant="default" onClick={sendSms} disabled={loading}>
                    SMS code instead
                  </Button>
                ) : null}
              </Group>
              {challenge?.methods.sms && (
                <Button variant="subtle" onClick={verifySms} disabled={loading}>
                  Verify SMS code
                </Button>
              )}
              <Button
                variant="subtle"
                size="xs"
                onClick={() => {
                  setPhase('password');
                  setChallenge(null);
                  setCode('');
                  setErr(null);
                }}
              >
                ← Back
              </Button>
            </>
          )}
        </Stack>
      </Card>
    </Center>
  );
}

// --- Shared bridge state hook (used by Triage + Index) ---


export function BroadcastSurface({
  broadcasts,
  onDismiss,
}: {
  broadcasts: Array<{
    id: string; title: string | null; body: string;
    image_url: string | null; video_url: string | null;
    link_url: string | null; link_text: string | null;
    severity: 'info' | 'warning' | 'critical';
    dismissable: boolean;
  }>;
  onDismiss: (id: string) => void;
}): React.JSX.Element | null {
  // Pick the highest-severity broadcast for the modal slot. Banners
  // stack but only one critical at a time.
  const critical = broadcasts.find((b) => b.severity === 'critical');
  const banners = broadcasts.filter((b) => b.severity !== 'critical');

  const renderMedia = (b: { image_url: string | null; video_url: string | null }): React.JSX.Element | null => {
    if (b.video_url) {
      // YouTube / Vimeo URL → embed. Otherwise <video> element.
      const yt = b.video_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
      if (yt) {
        return (
          <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 8, overflow: 'hidden' }}>
            <iframe
              src={`https://www.youtube.com/embed/${yt[1]}`}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
              allowFullScreen
              title="broadcast video"
            />
          </div>
        );
      }
      const vm = b.video_url.match(/vimeo\.com\/(\d+)/);
      if (vm) {
        return (
          <div style={{ position: 'relative', paddingTop: '56.25%', borderRadius: 8, overflow: 'hidden' }}>
            <iframe
              src={`https://player.vimeo.com/video/${vm[1]}`}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
              allowFullScreen
              title="broadcast video"
            />
          </div>
        );
      }
      return (
        <video
          src={b.video_url}
          controls
          style={{ width: '100%', maxHeight: 360, borderRadius: 8 }}
        />
      );
    }
    if (b.image_url) {
      return (
        <img
          src={b.image_url}
          alt={b.image_url}
          style={{ width: '100%', maxHeight: 320, objectFit: 'cover', borderRadius: 8 }}
        />
      );
    }
    return null;
  };

  return (
    <>
      {critical && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9500,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}
        >
          <Card
            withBorder
            padding="lg"
            radius="md"
            style={{ maxWidth: 600, width: '100%', maxHeight: '90vh', overflowY: 'auto', borderTop: '4px solid var(--mantine-color-red-6)' }}
          >
            <Stack gap="md">
              {critical.title && (
                <Title order={4} c="red.7">
                  {critical.title}
                </Title>
              )}
              {renderMedia(critical)}
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {critical.body}
              </Text>
              <Group justify="flex-end">
                {critical.link_url && (
                  <Button
                    component="a"
                    href={critical.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="red"
                  >
                    {critical.link_text ?? 'Open'}
                  </Button>
                )}
                {critical.dismissable && (
                  <Button variant="subtle" onClick={() => onDismiss(critical.id)}>
                    Dismiss
                  </Button>
                )}
              </Group>
            </Stack>
          </Card>
        </div>
      )}
      {banners.length > 0 && (
        <div
          style={{
            position: 'fixed',
            top: 56, // below AppShell header
            left: 240, // right of navbar
            right: 0,
            zIndex: 8000,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '8px 16px',
            pointerEvents: 'none',
          }}
        >
          {banners.map((b) => {
            const color = b.severity === 'warning' ? 'yellow' : 'blue';
            return (
              <Card
                key={b.id}
                withBorder
                padding="sm"
                radius="md"
                style={{
                  borderLeft: `4px solid var(--mantine-color-${color}-6)`,
                  background: `var(--mantine-color-${color}-0)`,
                  pointerEvents: 'auto',
                }}
              >
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Stack gap={4} style={{ flex: 1 }}>
                    {b.title && (
                      <Text fw={600} size="sm">
                        {b.title}
                      </Text>
                    )}
                    {(b.image_url || b.video_url) && (
                      <div style={{ marginBottom: 4 }}>{renderMedia(b)}</div>
                    )}
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                      {b.body}
                    </Text>
                    {b.link_url && (
                      <Group gap="xs" mt={4}>
                        <Button
                          size="xs"
                          variant="light"
                          color={color}
                          component="a"
                          href={b.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {b.link_text ?? 'Open'}
                        </Button>
                      </Group>
                    )}
                  </Stack>
                  {b.dismissable && (
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      onClick={() => onDismiss(b.id)}
                      aria-label="dismiss broadcast"
                    >
                      <IconX {...ICON_PROPS} />
                    </ActionIcon>
                  )}
                </Group>
              </Card>
            );
          })}
        </div>
      )}
    </>
  );
}

// --- Main addin shell ---


export type PaletteAction =
  | { kind: 'tab'; value: string; label: string; hint?: string }
  | { kind: 'case'; value: string; label: string; hint?: string }
  | { kind: 'preset'; value: string; label: string; hint?: string; payload: { q: string; filter: string; sender: string; scope: string } };
// Round 29 / v3.5.55: Organize-with-AI modal.
//
// Three-stage flow:
//   1. INPUT — user types a natural-language request like "Move all from
//      galadarilaw.com into Legal/Galadari"
//   2. PLAN — call /addin/organize/plan, show a preview: target folder,
//      matching count, sample of 8 emails. User can edit prompt or
//      confirm.
//   3. EXECUTE — for each matching email, post create_folder once + a
//      move_email per item via the addin's existing bridge commands.
//      Show progress; audit-log on completion.
//
// Per Hard Rule #2 spirit: nothing moves without explicit Confirm. The
// modal renders the full list of emails about to move so the user can
// see exactly what will happen.
export function OrganizeWithAIModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}): React.JSX.Element {
  type Plan = Extract<Awaited<ReturnType<typeof api.addinOrganizePlan>>, { applicable: true }>;
  type Stage = 'input' | 'planning' | 'plan' | 'executing' | 'done';
  const [stage, setStage] = useState<Stage>('input');
  const [prompt, setPrompt] = useState('');
  const [plan, setPlan] = useState<Plan | null>(null);
  const [rejection, setRejection] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: 0, succeeded: 0, failed: 0 });
  const [resolvedFolderPath, setResolvedFolderPath] = useState<string | null>(null);

  // Reset state every time the modal opens fresh.
  useEffect(() => {
    if (opened) {
      setStage('input');
      setPlan(null);
      setRejection(null);
      setError(null);
      setProgress({ done: 0, total: 0, succeeded: 0, failed: 0 });
      setResolvedFolderPath(null);
    }
  }, [opened]);

  // Listen for the addin's `folder_created` and `action_success`/`action_error`
  // bridge events while we're in the executing stage.
  useEffect(() => {
    if (!opened || stage !== 'executing') return;
    const handler = (e: { data: unknown }) => {
      try {
        const obj = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
        if (obj && typeof obj === 'object') {
          const o = obj as { type?: string; path?: string; action?: string; error?: string; message_id?: string };
          if (o.type === 'folder_created' && o.path) {
            setResolvedFolderPath(o.path);
          }
          if (o.type === 'action_success' && o.action === 'move') {
            setProgress((p) => ({ ...p, done: p.done + 1, succeeded: p.succeeded + 1 }));
          }
          if (o.type === 'action_error' && (o.action === 'Move' || o.action === 'move')) {
            setProgress((p) => ({ ...p, done: p.done + 1, failed: p.failed + 1 }));
          }
          if (o.type === 'folder_create_error' && o.error) {
            setError('Could not create folder: ' + o.error);
            setStage('input');
          }
        }
      } catch { /* ignore */ }
    };
    const wv = window.chrome?.webview;
    wv?.addEventListener('message', handler);
    return () => { try { wv?.removeEventListener('message', handler); } catch { /* ignore */ } };
  }, [opened, stage]);

  // When all moves are accounted for, finish + audit.
  useEffect(() => {
    if (stage !== 'executing') return;
    if (progress.total === 0) return;
    if (progress.done >= progress.total) {
      const summary = plan?.human_summary ?? '';
      const target = resolvedFolderPath ?? plan?.target_folder_path ?? '';
      api.addinOrganizeAudit({
        plan_summary: summary,
        count: progress.total,
        target_folder_path: target,
        succeeded: progress.succeeded,
        failed: progress.failed,
      }).catch(() => { /* best-effort */ });
      setStage('done');
    }
  }, [stage, progress, plan, resolvedFolderPath]);

  async function buildPlan(): Promise<void> {
    setStage('planning');
    setError(null);
    setRejection(null);
    try {
      const r = await api.addinOrganizePlan(prompt.trim());
      if (!r.applicable) {
        setRejection(r.rejection_reason);
        setStage('input');
        return;
      }
      setPlan(r);
      setStage('plan');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Plan failed');
      setStage('input');
    }
  }

  function executePlan(): void {
    if (!plan) return;
    const items = plan.matching.all_message_ids;
    if (items.length === 0) {
      setError('Nothing to move — the criteria matched zero emails.');
      return;
    }
    setProgress({ done: 0, total: items.length, succeeded: 0, failed: 0 });
    setStage('executing');
    // Step 1: ask the addin to create the target folder. The addin pushes
    // back `folder_created` with the resolved Outlook FolderPath.
    postToHost({ type: 'create_folder', path: plan.target_folder_path });
    // Step 2: small delay for folder creation, then move each item.
    // We rely on the addin processing messages in order — create_folder
    // first, then move_email each. If folder doesn't exist yet at the
    // moment a move_email lands, MoveEmail logs an action_error and
    // moves on.
    setTimeout(() => {
      // Use the resolved path if we got it back, otherwise fall back
      // to the relative path (addin's MoveEmail.ResolveFolderByPath
      // walks all stores looking for a match).
      const targetForMoves = resolvedFolderPath ?? plan.target_folder_path;
      for (const it of items) {
        postToHost({
          type: 'move_email',
          message_id: it.message_id,
          entry_id: it.message_id.startsWith('outlook:') ? it.message_id.slice('outlook:'.length) : null,
          target_folder: targetForMoves,
        });
      }
    }, 800);
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconSparkles size={18} />
          <Text fw={700}>Organize with AI</Text>
          <Badge size="xs" variant="light" color="violet">preview</Badge>
        </Group>
      }
      size="lg"
      closeOnClickOutside={stage !== 'executing'}
      closeOnEscape={stage !== 'executing'}
    >
      {stage === 'input' && (
        <Stack>
          <Text size="sm" c="dimmed">
            Describe what to move and where. The AI parses your request, previews matching emails,
            then asks for confirmation before any move happens.
          </Text>
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.currentTarget.value)}
            placeholder='e.g. "Move all from galadarilaw.com into Legal/Galadari"'
            minRows={3}
            autosize
            data-autofocus
          />
          {rejection && (
            <Alert color="orange" variant="light" icon={<IconAlertCircle size={14} />}>
              {rejection}
            </Alert>
          )}
          {error && (
            <Alert color="red" variant="light" icon={<IconAlertCircle size={14} />}>
              {error}
            </Alert>
          )}
          <Group justify="space-between">
            <Stack gap={2}>
              <Text size="xs" c="dimmed">Examples:</Text>
              <Text size="xs" c="dimmed">• Move all from elham@broadnetme.com into Team/Elham</Text>
              <Text size="xs" c="dimmed">• File newsletters from last 30 days into Newsletters</Text>
              <Text size="xs" c="dimmed">• Move emails about contracts into Legal/Contracts</Text>
            </Stack>
            <Button onClick={buildPlan} disabled={prompt.trim().length < 5} leftSection={<IconWand size={14} />}>
              Plan
            </Button>
          </Group>
        </Stack>
      )}
      {stage === 'planning' && (
        <Center py="xl"><Stack align="center"><Loader size="sm" /><Text size="sm" c="dimmed">Parsing your request…</Text></Stack></Center>
      )}
      {stage === 'plan' && plan && (
        <Stack>
          <Card withBorder p="sm">
            <Stack gap={4}>
              <Group gap="xs">
                <IconFolder size={14} />
                <Text size="sm" fw={600}>Target folder:</Text>
                <Code>{plan.target_folder_path}</Code>
              </Group>
              <Text size="sm">{plan.human_summary}</Text>
              <Group gap={4}>
                <Badge variant="light">{plan.matching.count} email{plan.matching.count === 1 ? '' : 's'} match</Badge>
                {plan.matching.count >= plan.matching.capped_at && (
                  <Badge variant="light" color="orange">capped at {plan.matching.capped_at}</Badge>
                )}
              </Group>
            </Stack>
          </Card>
          {plan.matching.count === 0 ? (
            <Alert color="gray" variant="light">No emails match. Try different criteria.</Alert>
          ) : (
            <Stack gap={4}>
              <Text size="xs" c="dimmed" fw={600}>SAMPLE — first {Math.min(plan.matching.sample.length, 8)} of {plan.matching.count}</Text>
              <Stack gap={2}>
                {plan.matching.sample.map((s) => (
                  <Box key={s.id} p={6} style={{ borderRadius: 4, background: 'var(--mantine-color-gray-0)' }}>
                    <Text size="xs" lineClamp={1} fw={500}>{s.subject ?? '(no subject)'}</Text>
                    <Text size="xs" c="dimmed" lineClamp={1}>{s.from_addr} · {new Date(s.received_at).toLocaleString()}</Text>
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}
          <Group justify="space-between">
            <Button variant="default" onClick={() => setStage('input')}>Back</Button>
            <Button color="violet" onClick={executePlan} disabled={plan.matching.count === 0} leftSection={<IconCheck size={14} />}>
              Confirm — Move {plan.matching.count} email{plan.matching.count === 1 ? '' : 's'}
            </Button>
          </Group>
        </Stack>
      )}
      {stage === 'executing' && (
        <Stack>
          <Group gap="xs"><Loader size="sm" /><Text size="sm">Moving emails…</Text></Group>
          <Progress
            value={progress.total === 0 ? 0 : (progress.done / progress.total) * 100}
            striped
            animated
          />
          <Text size="xs" c="dimmed">
            {progress.done} of {progress.total} processed · {progress.succeeded} moved · {progress.failed} failed
          </Text>
        </Stack>
      )}
      {stage === 'done' && (
        <Stack>
          <Alert
            color={progress.failed === 0 ? 'green' : 'orange'}
            variant="light"
            icon={<IconCheck size={14} />}
          >
            <Text fw={600}>Done.</Text>
            <Text size="sm">
              {progress.succeeded} email{progress.succeeded === 1 ? '' : 's'} moved
              {progress.failed > 0 && ` · ${progress.failed} failed`}
              {' '}into {resolvedFolderPath ?? plan?.target_folder_path ?? ''}.
            </Text>
          </Alert>
          <Group justify="flex-end">
            <Button onClick={onClose}>Close</Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

export function QuickPalette({
  opened,
  onClose,
  onPick,
}: {
  opened: boolean;
  onClose: () => void;
  onPick: (action: PaletteAction) => void;
}): React.JSX.Element {
  const [q, setQ] = useState('');
  const [cursor, setCursor] = useState(0);
  const [casesList, setCasesList] = useState<Awaited<ReturnType<typeof api.addinCasesList>>['cases']>([]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: open trigger
  useEffect(() => {
    if (!opened) return;
    setQ('');
    setCursor(0);
    api.addinCasesList().then((r) => setCasesList(r.cases)).catch(() => {});
  }, [opened]);

  const TABS: PaletteAction[] = [
    { kind: 'tab', value: 'action', label: 'Action Inbox', hint: 'Today' },
    { kind: 'tab', value: 'brief', label: 'Briefing', hint: 'Today' },
    { kind: 'tab', value: 'search', label: 'Search', hint: 'Find' },
    { kind: 'tab', value: 'ask', label: 'Ask AI', hint: 'Find' },
    { kind: 'tab', value: 'followups', label: 'Follow-ups', hint: 'Work' },
    { kind: 'tab', value: 'cases', label: 'Cases', hint: 'Work' },
    { kind: 'tab', value: 'people', label: 'People', hint: 'Work' },
    { kind: 'tab', value: 'insights', label: 'Insights', hint: 'Intelligence' },
    { kind: 'tab', value: 'changes', label: 'Changes', hint: 'Intelligence' },
    { kind: 'tab', value: 'suggestions', label: 'Suggestions', hint: 'Intelligence' },
    { kind: 'tab', value: 'rules', label: 'Rules', hint: 'Automation' },
    { kind: 'tab', value: 'activity', label: 'Activity log', hint: 'Automation' },
    { kind: 'tab', value: 'sources', label: 'Mail sources', hint: 'Automation' },
    { kind: 'tab', value: 'settings', label: 'Settings', hint: 'System' },
  ];
  const caseActions: PaletteAction[] = casesList.map((c) => ({
    kind: 'case', value: c.id, label: c.name, hint: `case · ${c.status} · ${c.message_count} msg`,
  }));
  let presetActions: PaletteAction[] = [];
  try {
    const raw = JSON.parse(localStorage.getItem('addin_search_presets') ?? '[]') as Array<{
      id: string; name: string; q: string; filter: string; sender: string; scope: string;
    }>;
    presetActions = raw.map((p) => ({
      kind: 'preset', value: p.id, label: p.name,
      hint: `preset · ${[p.q && `q: ${p.q}`, p.filter !== 'all' && p.filter, p.sender && `from: ${p.sender}`].filter(Boolean).join(' · ') || 'no filters'}`,
      payload: { q: p.q, filter: p.filter, sender: p.sender, scope: p.scope },
    }));
  } catch { /* ignore */ }
  const all: PaletteAction[] = [...TABS, ...caseActions, ...presetActions];
  const lower = q.trim().toLowerCase();
  const filtered = lower
    ? all.filter((a) => `${a.label} ${a.hint ?? ''}`.toLowerCase().includes(lower))
    : all;
  const max = Math.min(filtered.length - 1, Math.max(0, cursor));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      size="md"
      padding={0}
      transitionProps={{ duration: 80 }}
      title={null}
    >
      <Stack gap={0}>
        <TextInput
          size="md"
          placeholder='Jump to tab or case…'
          value={q}
          onChange={(e) => { setQ(e.currentTarget.value); setCursor(0); }}
          autoFocus
          variant="unstyled"
          styles={{ input: { fontSize: 16, padding: 12 } }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(filtered.length - 1, c + 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
            else if (e.key === 'Enter' && filtered[max]) { e.preventDefault(); onPick(filtered[max]); }
            else if (e.key === 'Escape') onClose();
          }}
        />
        <Divider />
        <ScrollArea h={Math.min(360, Math.max(60, filtered.length * 36))}>
          <Stack gap={0}>
            {filtered.length === 0 && (
              <Text size="sm" c="dimmed" p="md">No matches.</Text>
            )}
            {filtered.map((a, i) => (
              <Group
                key={`${a.kind}-${a.value}`}
                gap="xs"
                p="xs"
                style={{
                  cursor: 'pointer',
                  background: i === max ? 'var(--mantine-color-blue-0)' : undefined,
                }}
                onMouseEnter={() => setCursor(i)}
                onClick={() => onPick(a)}
              >
                <Badge size="xs" variant="dot" color={a.kind === 'case' ? 'blue' : a.kind === 'preset' ? 'violet' : 'gray'}>
                  {a.kind}
                </Badge>
                <Text size="sm" fw={500} style={{ flex: 1, minWidth: 0 }} lineClamp={1}>{a.label}</Text>
                {a.hint && <Text size="xs" c="dimmed">{a.hint}</Text>}
              </Group>
            ))}
          </Stack>
        </ScrollArea>
        <Divider />
        <Group gap="xs" p="xs" justify="space-between">
          <Text size="xs" c="dimmed">↑↓ navigate · Enter select · Esc close</Text>
          <Code style={{ fontSize: 10 }}>⌘ P</Code>
        </Group>
      </Stack>
    </Modal>
  );
}

// Bell icon in header — opens a Drawer of recent toasts.
export function NotificationHistoryButton(): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(0);
  const [q, setQ] = useState('');
  useEffect(() => {
    const handler = () => setTick((x) => x + 1);
    window.addEventListener('ai-outlook:notif-history-update', handler);
    return () => window.removeEventListener('ai-outlook:notif-history-update', handler);
  }, []);
  void tick; // re-render on event
  const lower = q.trim().toLowerCase();
  const visible = lower
    ? notifHistory.filter((n) => n.message.toLowerCase().includes(lower))
    : notifHistory;
  return (
    <>
      <Tooltip label={`Notifications (${notifHistory.length})`}>
        <ActionIcon size="sm" variant="default" onClick={() => setOpen(true)} aria-label="Notifications">
          <IconHistory {...ICON_PROPS} />
        </ActionIcon>
      </Tooltip>
      <Drawer opened={open} onClose={() => setOpen(false)} position="right" size="md"
        title={<Group gap="xs"><IconHistory {...ICON_PROPS} /><Text fw={600}>Notifications</Text>
          <Badge size="xs" variant="light">{notifHistory.length}</Badge></Group>}>
        <Stack gap="xs">
          {notifHistory.length === 0 ? (
            <Text size="sm" c="dimmed">No notifications yet.</Text>
          ) : (
            <>
              <Group gap="xs" wrap="nowrap">
                <TextInput
                  size="xs"
                  placeholder='Filter notifications…'
                  value={q}
                  onChange={(e) => setQ(e.currentTarget.value)}
                  rightSection={q ? (
                    <ActionIcon size="xs" variant="subtle" color="gray" onClick={() => setQ('')}>
                      <IconX size={10} />
                    </ActionIcon>
                  ) : null}
                  style={{ flex: 1 }}
                />
                <Button size="compact-xs" variant="subtle" color="gray"
                  onClick={() => { notifHistory.length = 0; persistNotifHistory(); setOpen(false); }}>
                  Clear all
                </Button>
              </Group>
              {visible.length === 0 && q && (
                <Text size="xs" c="dimmed" ta="center" py="md">No notifications match "{q}".</Text>
              )}
              {visible.map((n) => (
                <Paper key={n.id} p="xs" withBorder
                  style={{ borderLeft: `3px solid var(--mantine-color-${n.color}-5)` }}>
                  <Stack gap={0}>
                    <Text size="sm">{n.message || '(no message)'}</Text>
                    <Text size="xs" c="dimmed">{formatRelative(n.ts)}</Text>
                  </Stack>
                </Paper>
              ))}
            </>
          )}
        </Stack>
      </Drawer>
    </>
  );
}

export function Sidebar({
  activeTab,
  setActiveTab,
  indexRunning,
  overduePromisesCount = 0,
  reminderBadge = { pending: 0, fired: 0 },
}: {
  activeTab: string;
  setActiveTab: (t: string) => void;
  indexRunning: boolean;
  overduePromisesCount?: number;
  reminderBadge?: { pending: number; fired: number };
}): React.JSX.Element {
  const [pinned, setPinned] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('addin_sidebar_pinned') ?? '[]') as string[]; }
    catch { return []; }
  });
  function togglePin(value: string) {
    setPinned((prev) => {
      const next = prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value];
      localStorage.setItem('addin_sidebar_pinned', JSON.stringify(next));
      return next;
    });
  }
  function movePinned(value: string, dir: -1 | 1) {
    setPinned((prev) => {
      const i = prev.indexOf(value);
      if (i < 0) return prev;
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      const vi = next[i];
      const vj = next[j];
      if (vi !== undefined && vj !== undefined) {
        next[i] = vj;
        next[j] = vi;
      }
      localStorage.setItem('addin_sidebar_pinned', JSON.stringify(next));
      return next;
    });
  }
  const groups: Array<{
    key: string;
    label: string;
    icon: React.JSX.Element;
    defaultOpen?: boolean;
    items: Array<{
      value: string;
      label: string;
      icon: React.JSX.Element;
      rightBadge?: React.JSX.Element | undefined;
    }>;
  }> = [
    {
      key: 'today',
      label: 'Today',
      icon: <IconTarget {...ICON_PROPS} />,
      defaultOpen: true,  // first group expanded by default — most-used
      items: [
        {
          value: 'action',
          label: 'Action Inbox',
          icon: <IconTarget {...ICON_PROPS} />,
          rightBadge: overduePromisesCount > 0
            ? <Tooltip label={`${overduePromisesCount} overdue promise${overduePromisesCount === 1 ? '' : 's'}`}>
                <Badge size="xs" color="red" variant="filled">{overduePromisesCount}</Badge>
              </Tooltip>
            : undefined,
        },
        { value: 'brief', label: 'Briefing', icon: <IconNews {...ICON_PROPS} /> },
        { value: 'workspace', label: 'Workspace', icon: <IconBriefcase {...ICON_PROPS} /> },
      ],
    },
    {
      key: 'find',
      label: 'Find',
      icon: <IconSearch {...ICON_PROPS} />,
      items: [
        { value: 'search', label: 'Search', icon: <IconSearch {...ICON_PROPS} /> },
        { value: 'ask', label: 'Ask AI', icon: <IconMessageQuestion {...ICON_PROPS} /> },
      ],
    },
    {
      key: 'work',
      label: 'Work',
      icon: <IconFolder {...ICON_PROPS} />,
      items: [
        { value: 'followups', label: 'Follow-ups', icon: <IconFlag {...ICON_PROPS} /> },
        { value: 'cases', label: 'Cases', icon: <IconFolder {...ICON_PROPS} /> },
        { value: 'people', label: 'People', icon: <IconUsersGroup {...ICON_PROPS} /> },
        { value: 'calendar', label: 'Calendar', icon: <IconCalendar {...ICON_PROPS} /> },
      ],
    },
    {
      key: 'intelligence',
      label: 'Intelligence',
      icon: <IconBrain {...ICON_PROPS} />,
      items: [
        { value: 'insights', label: 'Insights', icon: <IconChartBar {...ICON_PROPS} /> },
        { value: 'changes', label: 'Changes', icon: <IconRadar {...ICON_PROPS} /> },
        { value: 'suggestions', label: 'Suggestions', icon: <IconWand {...ICON_PROPS} /> },
      ],
    },
    {
      key: 'automation',
      label: 'Automation',
      icon: <IconWand {...ICON_PROPS} />,
      items: [
        { value: 'rules', label: 'Rules', icon: <IconBrain {...ICON_PROPS} /> },
        { value: 'activity', label: 'Activity log', icon: <IconHistory {...ICON_PROPS} /> },
        {
          value: 'sources',
          label: 'Mail sources',
          icon: <IconBolt {...ICON_PROPS} />,
          rightBadge: indexRunning ? <Badge size="xs" color="blue" variant="filled">●</Badge> : undefined,
        },
      ],
    },
    {
      key: 'system',
      label: 'System',
      icon: <IconSettings {...ICON_PROPS} />,
      defaultOpen: true, // Round 28-L: Settings was hidden behind collapsed
                         // group — that's why "settings page not opening" was
                         // reported. Always show.
      items: [
        { value: 'settings', label: 'Settings', icon: <IconSettings {...ICON_PROPS} /> },
        { value: 'goals', label: 'Goals', icon: <IconFlag {...ICON_PROPS} /> },
        { value: 'autopilot', label: 'Autopilot', icon: <IconBrain {...ICON_PROPS} /> },
        { value: 'templates', label: 'Templates', icon: <IconFileText {...ICON_PROPS} /> },
        {
          value: 'reminders',
          label: 'Reminders',
          icon: <IconClock {...ICON_PROPS} />,
          // Round 28-L Phase 1.3b: red badge for fired (need attention),
          // gray badge for pending count. Fired wins display when both > 0.
          rightBadge: reminderBadge.fired > 0
            ? <Tooltip label={`${reminderBadge.fired} fired reminder${reminderBadge.fired === 1 ? '' : 's'} need attention`}>
                <Badge size="xs" color="red" variant="filled">{reminderBadge.fired}</Badge>
              </Tooltip>
            : reminderBadge.pending > 0
              ? <Tooltip label={`${reminderBadge.pending} pending reminder${reminderBadge.pending === 1 ? '' : 's'}`}>
                  <Badge size="xs" color="gray" variant="light">{reminderBadge.pending}</Badge>
                </Tooltip>
              : undefined,
        },
      ],
    },
  ];

  // Round 47: collapsible sections. State persists to localStorage so the
  // user's open/closed choice survives reloads. Group containing the
  // currently-active item is force-opened so the user always sees where
  // they are even if they collapsed it earlier.
  const STORAGE_KEY = 'addin_sidebar_open';
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored) as Record<string, boolean>;
    } catch { /* ignore */ }
    // Default: only the first group ('today') is open.
    return Object.fromEntries(groups.map((g) => [g.key, !!g.defaultOpen]));
  });
  function toggleGroup(key: string) {
    setOpenGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }

  // Flatten all sidebar items so the pinned strip can resolve them by value.
  const allItems = groups.flatMap((g) => g.items);
  const pinnedItems = pinned
    .map((v) => allItems.find((it) => it.value === v))
    .filter((x): x is typeof allItems[number] => !!x);

  return (
    <AppShell.Section grow component={ScrollArea}>
      <Stack gap={4} pr="xs">
        {pinnedItems.length > 0 && (
          <Stack gap={2} mb={4}>
            <Text size="xs" c="dimmed" tt="uppercase" pl="xs">Pinned</Text>
            {pinnedItems.map((it, idx) => (
              <NavLink
                key={`pin-${it.value}`}
                label={`★ ${it.label}`}
                leftSection={it.icon}
                rightSection={
                  <Group gap={2} wrap="nowrap">
                    {idx > 0 && (
                      <Tooltip label="Move up" withinPortal openDelay={400}>
                        <ActionIcon size="xs" variant="subtle" color="gray"
                          onClick={(e) => { e.stopPropagation(); movePinned(it.value, -1); }}
                          aria-label={`Move ${it.label} up`}>
                          <IconChevronUp size={10} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    {idx < pinnedItems.length - 1 && (
                      <Tooltip label="Move down" withinPortal openDelay={400}>
                        <ActionIcon size="xs" variant="subtle" color="gray"
                          onClick={(e) => { e.stopPropagation(); movePinned(it.value, 1); }}
                          aria-label={`Move ${it.label} down`}>
                          <IconChevronDown size={10} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    {it.rightBadge}
                  </Group>
                }
                active={activeTab === it.value}
                onClick={() => setActiveTab(it.value)}
                onContextMenu={(e) => { e.preventDefault(); togglePin(it.value); }}
                variant="filled"
              />
            ))}
            <Divider mt={4} />
          </Stack>
        )}
        {groups.map((g) => {
          // Round 47: respect the user's explicit choice. Previous version
          // force-opened the group containing the active tab, which made it
          // impossible to collapse. Now the user fully controls open/closed.
          const isOpen = openGroups[g.key] === true;
          return (
            <Stack key={g.key} gap={2}>
              <NavLink
                label={g.label}
                leftSection={g.icon}
                opened={isOpen}
                onClick={() => toggleGroup(g.key)}
                childrenOffset={28}
                styles={{
                  label: { fontWeight: 600, letterSpacing: 0.3 },
                }}
              >
                {g.items.map((it) => {
                  // Map keyboard shortcut numbers (matches the global handler).
                  const tabsOrder = ['action', 'insights', 'search', 'triage', 'brief', 'followups', 'cases', 'rules', 'audit', 'index', 'settings'];
                  const idx = tabsOrder.indexOf(it.value);
                  const tip = idx >= 0 && idx < 9 ? `${it.label} — press ${idx + 1} · right-click to ${pinned.includes(it.value) ? 'unpin' : 'pin'}` : `${it.label} · right-click to ${pinned.includes(it.value) ? 'unpin' : 'pin'}`;
                  return (
                    <Tooltip key={it.value} label={tip} position="right" withinPortal openDelay={400}>
                      <NavLink
                        label={pinned.includes(it.value) ? `★ ${it.label}` : it.label}
                        leftSection={it.icon}
                        rightSection={it.rightBadge}
                        active={activeTab === it.value}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTab(it.value);
                        }}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          togglePin(it.value);
                        }}
                        variant="filled"
                      />
                    </Tooltip>
                  );
                })}
              </NavLink>
            </Stack>
          );
        })}
      </Stack>
    </AppShell.Section>
  );
}

// Top-bar global search/ask input. Enter → search, ⌘+Enter → ask AI.
// Lives in the AppShell header so it's always reachable.
export function GlobalSearchInput({
  onSubmit,
  onOpenCommandBar,
}: {
  onSubmit: (q: string, mode: 'search' | 'ask') => void;
  onOpenCommandBar: () => void;
}): React.JSX.Element {
  const [q, setQ] = useState('');
  return (
    <TextInput
      placeholder="Search or ask anything…  (⌘K for actions)"
      value={q}
      onChange={(e) => setQ(e.currentTarget.value)}
      style={{ flex: 1, maxWidth: 720 }}
      leftSection={<IconSearch size={14} stroke={1.5} />}
      rightSection={
        <Tooltip label="Open command bar (Ctrl+K)">
          <ActionIcon variant="subtle" size="sm" onClick={onOpenCommandBar}>
            <IconCommand size={14} stroke={1.5} />
          </ActionIcon>
        </Tooltip>
      }
      onKeyDown={(e) => {
        if (e.key === 'Enter' && q.trim().length > 0) {
          const mode: 'search' | 'ask' = (e.metaKey || e.ctrlKey) ? 'ask' : 'search';
          onSubmit(q.trim(), mode);
          setQ('');
        }
      }}
    />
  );
}

// People panel — top correspondents (humans only). Click to open the
// existing sender drawer for full profile + VIP toggle + recent emails.
// Round 29 / v3.5.46: Calendar viewer. Reads from /addin/calendar/upcoming
// (populated by the addin's SyncCalendarAsync). Groups appointments by
// date with relative-day labels (today/tomorrow/this week). Shows
// attendees, location, online meeting link, response status.
